import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cake, MessageCircle, Send, Sparkles, Check, Image as ImageIcon } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { AuthImage } from "./FileUpload";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { MonthNav, WhatsAppLink, thisMonth, waLink } from "./MonthNav";
import { BirthdayOptOut } from "./BirthdayOptOut";
import { ShareImageButton } from "./ShareImageButton";



function Avatar({ b, size = "h-12 w-12" }) {
  return (
    <div className={`${size} flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-crimson text-lg font-bold text-parchment ${b.is_today ? "ring-2 ring-gold" : ""}`}>
      {b.photo_url ? <AuthImage src={b.photo_url} alt={b.name} className="h-full w-full object-cover" /> : b.name?.[0]}
    </div>
  );
}

function CelebrateDialog({ b, templates, onClose, onSent }) {
  const [msg, setMsg] = useState(templates[0].replace("{name}", b.name));
  const [opts, setOpts] = useState({ notify_member: true, announce_all: true, send_email: true });
  const [busy, setBusy] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  const shareCard = async () => {
    setCardBusy(true);
    try {
      const r = await api.get(`/admin/birthdays/${b.id}/card.png?message=${encodeURIComponent(msg)}`, { responseType: "blob" });
      const file = new File([r.data], `birthday-${b.name.replace(/\s+/g, "-").toLowerCase()}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text: `🎂 ${msg}` }); return; }
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
      toast.success("Card downloaded — attach it in WhatsApp");
    } catch (e) { if (e?.name !== "AbortError") toast.error(formatError(e.response?.data?.detail) || "Could not build the card"); }
    finally { setCardBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/birthdays/celebrate", { member_id: b.id, message: msg, ...opts }); toast.success(`Celebration sent${data.announced_to ? ` · announced to ${data.announced_to} members` : ""}`); onSent(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="celebrate-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">Celebrate {b.name} · {b.date_label}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">{templates.map((t, i) => <button key={t.slice(0, 20)} onClick={() => setMsg(t.replace("{name}", b.name))} className="rounded-full border border-gold/50 px-3 py-1 text-xs text-crimson hover:bg-gold-light" data-testid={`celebrate-template-${i}`}><Sparkles size={11} className="mr-1 inline" />Message {i + 1}</button>)}</div>
        <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="celebrate-message" />
        <div className="space-y-2 text-sm">
          <label className="flex items-center justify-between"><span>Send greeting to {b.name.split(" ")[0]} (push{b.has_email ? " + email" : ""})</span><Switch checked={opts.notify_member} onCheckedChange={(v) => setOpts({ ...opts, notify_member: v })} data-testid="celebrate-notify-member" /></label>
          <label className="flex items-center justify-between"><span>Announce to all members (push notification)</span><Switch checked={opts.announce_all} onCheckedChange={(v) => setOpts({ ...opts, announce_all: v })} data-testid="celebrate-announce-all" /></label>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={shareCard} disabled={cardBusy} className="gap-1 border-gold text-crimson" data-testid="celebrate-card"><ImageIcon size={14} /> {cardBusy ? "Preparing…" : "Birthday card image"}</Button>
          <a href={waLink(`🎂 ${msg}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-[#25D366] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1ebe5a]" data-testid="celebrate-whatsapp"><MessageCircle size={14} /> Share on WhatsApp</a>
          <Button onClick={send} disabled={busy || msg.trim().length < 10} className="gap-1 bg-crimson hover:bg-crimson-hover" data-testid="celebrate-send"><Send size={14} /> {busy ? "Sending…" : "Send celebration"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BirthdayRow({ b, onCelebrate }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-3 ${b.is_today ? "border-gold bg-gold-light" : "border-gold/20 bg-parchment-card"}`} data-testid={`birthday-row-${b.id}`}>
      <Avatar b={b} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-crimson">{b.name}</p>
        <p className="text-xs text-muted-foreground">{b.date_label}{b.is_today ? " · Today!" : ""}{b.member_id ? ` · ${b.member_id}` : ""}</p>
        {b.celebrated && <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-palm" data-testid={`birthday-celebrated-${b.id}`}><Check size={11} /> Celebrated by {b.celebrated_by}</p>}
      </div>
      <Button size="sm" variant={b.celebrated ? "outline" : "default"} onClick={onCelebrate} className={b.celebrated ? "" : "bg-crimson hover:bg-crimson-hover"} data-testid={`birthday-celebrate-${b.id}`}>{b.celebrated ? "Again" : "Celebrate"}</Button>
    </div>
  );
}

const monthSummary = (label, list) => `🎉 ${label} birthdays — Okuapemman Association of Massachusetts:\n${list.map((b) => `• ${b.name} — ${b.date_label}`).join("\n")}\nJoin us in celebrating our members! Ofie! Biakoye!`;

export function BirthdayCelebrations() {
  const [month, setMonth] = useState(thisMonth());
  const { data, reload } = useApi(`/admin/birthdays?month=${month}`);
  const [sel, setSel] = useState(null);
  const list = data?.birthdays || [];
  useEffect(() => { setSel(null); }, [month]);
  return (
    <div className="glass rounded-2xl p-6" data-testid="birthday-celebrations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-palm"><Cake size={20} className="text-gold" /> Birthday celebrations</h3>
        <MonthNav month={month} label={data?.month_label} onChange={setMonth} testId="birthdays" />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{list.length} member{list.length === 1 ? "" : "s"} celebrating this month{data?.hidden ? ` (${data.hidden} hidden by request)` : ""}. Send a greeting to the member, announce it to everyone, or share the list on WhatsApp.</p>
      {data && list.length === 0 && <p className="mt-4 text-sm text-muted-foreground" data-testid="birthdays-empty">No member birthdays on record for this month.</p>}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {list.map((b) => <BirthdayRow key={b.id} b={b} onCelebrate={() => setSel(b)} />)}
      </div>
      {list.length > 0 && <div className="mt-4 flex flex-wrap gap-2"><WhatsAppLink text={monthSummary(data.month_label, list)} testId="birthdays-share-month"><MessageCircle size={14} /> Share this month's list on WhatsApp</WhatsAppLink>
        <ShareImageButton url={`/admin/birthdays/month-card.png?month=${month}`} filename={`birthdays-${month}.png`} text={monthSummary(data.month_label, list)} label="Share list as branded image" testId="birthdays-share-month-image" /></div>}
      <BirthdayOptOut onChanged={reload} />
      {sel && data && <CelebrateDialog b={sel} templates={data.templates} onClose={() => setSel(null)} onSent={() => { setSel(null); reload(); }} />}
    </div>
  );
}
